import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ArrowDown, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  engagementPersonaDedupeKey,
  formatAccountFitIntegerDisplay,
  formatLastTouchCalendarLabel,
  formatLastTouchTooltip,
  formatNextScheduled,
  formatRel,
  getAccountProfileUrl,
  getLastActivityAt,
  getLastTouchAt,
  getNextScheduledRaw,
  getPersonProfileUrl,
  getPersonaArchetypeName,
  isSameLocalDay,
  resolvePersonaTouches,
} from '@/lib/engagementPersonaUtils';

export default function EngagementPersonaTable({
  rows,
  sortConfig,
  onToggleSort,
  emptyMessage,
  getAccountFitScoreForRow,
  accountIdByNameMap,
}) {
  const renderSortArrow = (key) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-3 h-3" />
      : <ArrowDown className="w-3 h-3" />;
  };

  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full min-w-[1120px] text-sm table-fixed">
          <colgroup>
            <col style={{ width: '13%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
          </colgroup>
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
            <tr>
              <th className="px-4 py-2 text-left w-[13%]">
                <button type="button" onClick={() => onToggleSort?.('person_name')} className="inline-flex items-center gap-1">
                  Persona {renderSortArrow('person_name')}
                </button>
              </th>
              <th className="px-4 py-2 text-left w-[11%]">
                <button type="button" onClick={() => onToggleSort?.('account_name')} className="inline-flex items-center gap-1">
                  Account {renderSortArrow('account_name')}
                </button>
              </th>
              <th className="px-4 py-2 text-center w-[8%]">
                <button type="button" onClick={() => onToggleSort?.('account_fit_score')} className="inline-flex items-center justify-center gap-1 w-full">
                  Account fit {renderSortArrow('account_fit_score')}
                </button>
              </th>
              <th className="px-4 py-2 text-center w-[9%]">
                <button type="button" onClick={() => onToggleSort?.('archetype_name')} className="inline-flex items-center justify-center gap-1 w-full">
                  Archetype {renderSortArrow('archetype_name')}
                </button>
              </th>
              <th className="px-4 py-2 text-center w-[7%]">
                <button type="button" onClick={() => onToggleSort?.('persona_fit_score')} className="inline-flex items-center justify-center gap-1 w-full">
                  Persona fit {renderSortArrow('persona_fit_score')}
                </button>
              </th>
              <th className="px-4 py-2 text-center w-[7%]">
                <button type="button" onClick={() => onToggleSort?.('touches_count')} className="inline-flex items-center justify-center gap-1 w-full">
                  Touches {renderSortArrow('touches_count')}
                </button>
              </th>
              <th className="px-4 py-2 text-center w-[8%]">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" onClick={() => onToggleSort?.('last_touch_at')} className="inline-flex items-center justify-center gap-1 w-full">
                        Last touch {renderSortArrow('last_touch_at')}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      Uses calendar days in your time zone (same buckets as the Last touch filter), not elapsed 24-hour periods.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
              <th className="px-4 py-2 text-center w-[7%]">
                <button type="button" onClick={() => onToggleSort?.('human_opens')} className="inline-flex items-center justify-center gap-1 w-full">
                  True opens {renderSortArrow('human_opens')}
                </button>
              </th>
              <th className="px-4 py-2 text-center w-[7%]">
                <button type="button" onClick={() => onToggleSort?.('human_clicks')} className="inline-flex items-center justify-center gap-1 w-full">
                  True clicks {renderSortArrow('human_clicks')}
                </button>
              </th>
              <th className="px-4 py-2 text-center w-[8%]">
                <button type="button" onClick={() => onToggleSort?.('last_activity_at')} className="inline-flex items-center justify-center gap-1 w-full">
                  Last activity {renderSortArrow('last_activity_at')}
                </button>
              </th>
              <th className="px-4 py-2 text-center w-[8%]">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" onClick={() => onToggleSort?.('next_scheduled')} className="inline-flex items-center justify-center gap-1 w-full">
                        Next scheduled {renderSortArrow('next_scheduled')}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      The next time an auto-send message will fire for this persona — guaranteed.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
              <th className="px-4 py-2 text-center w-[8%]">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" onClick={() => onToggleSort?.('linkedin_connected')} className="inline-flex items-center justify-center gap-1 w-full whitespace-nowrap">
                        LinkedIn {renderSortArrow('linkedin_connected')}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      LinkedIn connected — true when a LinkedIn DM has been marked sent or done for this persona.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
            </tr>
          </thead>
          <tbody>
            {!rows?.length ? (
              <tr>
                <td colSpan={12} className="px-4 py-12 text-center text-sm text-gray-500">
                  {emptyMessage || 'No personas match the current filters.'}
                </td>
              </tr>
            ) : (
              rows.map((p, idx) => {
                const hasTodayEngagement =
                  isSameLocalDay(p.last_human_open_at) || isSameLocalDay(p.last_human_click_at);
                const rowClass = hasTodayEngagement
                  ? 'border-t bg-[#EAFBF5] hover:bg-[#DDF7EE]'
                  : 'border-t hover:bg-gray-50';
                const profileUrl = getPersonProfileUrl(p);
                const accountUrl = getAccountProfileUrl(p, accountIdByNameMap);
                const displayName = p.person_name || p.person_email || '—';
                const accountDisplay = p.account_name || '—';
                return (
                  <tr key={engagementPersonaDedupeKey(p, idx)} className={rowClass}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-[#0A1A2F] truncate">
                        {profileUrl ? (
                          <Link
                            to={profileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#0A1A2F] hover:text-[#00A3A3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
                          >
                            {displayName}
                          </Link>
                        ) : (
                          <span>{displayName}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{p.person_email}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-700 truncate">
                      {accountUrl ? (
                        <Link
                          to={accountUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0A1A2F] hover:text-[#00A3A3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
                        >
                          {accountDisplay}
                        </Link>
                      ) : (
                        <span>{accountDisplay}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-gray-700 whitespace-nowrap">
                      {formatAccountFitIntegerDisplay(getAccountFitScoreForRow?.(p))}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="block text-gray-700 break-words leading-snug" title={getPersonaArchetypeName(p) || undefined}>
                        {getPersonaArchetypeName(p) || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center text-gray-700 whitespace-nowrap">
                      {p.persona_fit_score !== null && p.persona_fit_score !== undefined ? p.persona_fit_score : '—'}
                    </td>
                    <td className="px-4 py-2 text-center text-sm text-gray-700 whitespace-nowrap">
                      {resolvePersonaTouches(p)}
                    </td>
                    <td className="px-4 py-2 text-center text-xs text-gray-600 whitespace-nowrap">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block">{formatLastTouchCalendarLabel(getLastTouchAt(p))}</span>
                          </TooltipTrigger>
                          <TooltipContent>{formatLastTouchTooltip(getLastTouchAt(p))}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="px-4 py-2 text-center text-sm text-gray-700 whitespace-nowrap">
                      {p.human_opens ?? 0}
                    </td>
                    <td className="px-4 py-2 text-center text-sm text-gray-700 whitespace-nowrap">
                      {p.human_clicks ?? 0}
                    </td>
                    <td className="px-4 py-2 text-center text-xs text-gray-600 whitespace-nowrap">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block">{formatRel(getLastActivityAt(p))}</span>
                          </TooltipTrigger>
                          <TooltipContent>{getLastActivityAt(p) || 'never'}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="px-4 py-2 text-center text-xs text-gray-600 whitespace-nowrap">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block">{formatNextScheduled(getNextScheduledRaw(p))}</span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            {getNextScheduledRaw(p) ||
                              "No auto-send queued. May have drafts awaiting approval — check the persona's outbox."}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="px-4 py-2 text-center text-sm whitespace-nowrap">
                      {p.linkedin_connected === true ||
                      p.linkedin_connected === 1 ||
                      p.linkedin_connected === 'true' ? (
                        <span className="inline-flex items-center justify-center" title="LinkedIn DM sent / done">
                          <Check className="w-4 h-4 text-emerald-600" aria-label="Yes" />
                        </span>
                      ) : (
                        <span className="text-gray-400" title="No LinkedIn DM marked done">
                          N
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
